/**
 * Styles partagés pour les pages du portail.
 * Extraction des 120+ redéfinitions dispersées dans 62 fichiers (audit 2026-04-16).
 *
 * Usage :
 *   import { HDR_BTN, EDIT_INPUT, FIELD_LABEL, TH_STYLE, TD_STYLE } from '../styles/CommonStyles';
 *
 *   <button style={HDR_BTN}>Action</button>
 *
 * Les styles sont exportés comme **const objects** (spread-safe, mémo-friendly).
 * Variants avec `{ ...HDR_BTN, background: '#custom' }` pour overrider un champ.
 */

// ── Buttons ─────────────────────────────────────────────────────────────────

export const HDR_BTN = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 14px',
  border: 'none',
  borderRadius: 6,
  color: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  background: 'var(--primary-light)',
  fontFamily: "'IBM Plex Sans', sans-serif",
  transition: 'background 0.15s, opacity 0.15s',
};

export const PRIMARY_BTN = { ...HDR_BTN, background: '#2563eb' };
export const SUCCESS_BTN = { ...HDR_BTN, background: '#059669' };
export const WARNING_BTN = { ...HDR_BTN, background: '#f59e0b' };
export const DANGER_BTN = { ...HDR_BTN, background: '#dc2626' };
export const NEUTRAL_BTN = { ...HDR_BTN, background: '#64748b' };

// Small icon button (toolbar actions)
export const ICON_BTN = {
  background: 'none',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: 4,
  cursor: 'pointer',
  color: 'var(--primary)',
  display: 'flex',
  transition: 'all var(--duration-fast)',
};

// ── Forms ───────────────────────────────────────────────────────────────────

export const INPUT_STYLE = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  boxSizing: 'border-box',
  fontFamily: "'IBM Plex Sans', sans-serif",
  transition: 'border-color var(--duration-fast), box-shadow var(--duration-fast)',
};

// Variante compacte pour les formulaires inline (détail page avec bcp de champs)
export const EDIT_INPUT = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #93c5fd',
  fontSize: '0.85rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

export const SELECT_STYLE = {
  padding: '9px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  background: 'var(--surface)',
  color: 'var(--primary)',
  cursor: 'pointer',
  fontFamily: "'IBM Plex Sans', sans-serif",
  transition: 'border-color var(--duration-fast)',
};

// Label de champ (au-dessus d'un input)
export const FIELD_LABEL = {
  display: 'block',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: '#64748b',
  marginBottom: 3,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

// ── Tables ──────────────────────────────────────────────────────────────────

export const TH_STYLE = {
  padding: '8px 10px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: '#64748b',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg)',
};

export const TD_STYLE = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border-light)',
  fontSize: 13,
  color: 'var(--primary)',
};

// ── Modals ──────────────────────────────────────────────────────────────────

export const OVERLAY_STYLE = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(10,20,35,0.5)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

export const MODAL_CONTENT = {
  background: '#fff',
  borderRadius: 8,
  width: 700,
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};

export const MODAL_HEADER = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  background: '#1a3a5c',
  color: '#fff',
  borderRadius: '8px 8px 0 0',
  fontWeight: 700,
  fontSize: 14,
};

// ── Exports groupés pour import plus concis ─────────────────────────────────

export const BUTTON_STYLES = {
  hdr: HDR_BTN,
  primary: PRIMARY_BTN,
  success: SUCCESS_BTN,
  warning: WARNING_BTN,
  danger: DANGER_BTN,
  neutral: NEUTRAL_BTN,
  icon: ICON_BTN,
};

export const FORM_STYLES = {
  input: INPUT_STYLE,
  editInput: EDIT_INPUT,
  select: SELECT_STYLE,
  fieldLabel: FIELD_LABEL,
};

export const TABLE_STYLES = {
  th: TH_STYLE,
  td: TD_STYLE,
};

export const MODAL_STYLES = {
  overlay: OVERLAY_STYLE,
  content: MODAL_CONTENT,
  header: MODAL_HEADER,
};
